import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, Folder, HardDrive, Search } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { DocumentoList } from "@/components/documentos/DocumentoList";
import { UploadDocumentDialog } from "@/components/documentos/UploadDocumentDialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { DEFAULT_DOCUMENTOS_CONFIG, formatBytes, subcarpetaLabel } from "@/lib/documentos";
import { documentosService } from "@/services/documentos.service";
import type { Documento, DocumentosConfig, DocumentosTree } from "@/types";

export const Route = createFileRoute("/documentos")({
  head: () => ({ meta: [{ title: "Gestión documental — LicitaPro" }] }),
  component: Page,
});

interface Selection {
  ambito: "empresa" | "licitacion";
  licitacionId: string | null;
  licitacionCodigo: string | null;
  subcarpeta: string | null;
}

const EMPTY_TREE: DocumentosTree = {
  totalDocumentos: 0,
  totalBytes: 0,
  empresa: { total: 0, subcarpetas: [] },
  licitaciones: [],
};

function Page() {
  const [config, setConfig] = useState<DocumentosConfig>(DEFAULT_DOCUMENTOS_CONFIG);
  const [tree, setTree] = useState<DocumentosTree>(EMPTY_TREE);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [selection, setSelection] = useState<Selection>({
    ambito: "empresa",
    licitacionId: null,
    licitacionCodigo: null,
    subcarpeta: null,
  });
  const [query, setQuery] = useState("");
  const [loadingTree, setLoadingTree] = useState(true);
  const [loadingDocs, setLoadingDocs] = useState(true);

  const loadTree = useCallback(async () => {
    setLoadingTree(true);
    try {
      const [cfg, arbol] = await Promise.all([documentosService.config(), documentosService.tree()]);
      setConfig(cfg);
      setTree(arbol);
    } catch {
      toast.error("No se pudo cargar la estructura de carpetas");
    } finally {
      setLoadingTree(false);
    }
  }, []);

  const loadDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      setDocumentos(
        await documentosService.list({
          ambito: selection.ambito,
          licitacionId: selection.licitacionId ?? undefined,
          subcarpeta: selection.subcarpeta ?? undefined,
          q: query.trim() || undefined,
        }),
      );
    } catch {
      toast.error("No se pudieron cargar los documentos");
    } finally {
      setLoadingDocs(false);
    }
  }, [selection, query]);

  useEffect(() => {
    void loadTree();
  }, [loadTree]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadDocs(), 250);
    return () => window.clearTimeout(timer);
  }, [loadDocs]);

  const refresh = useCallback(() => {
    void loadTree();
    void loadDocs();
  }, [loadTree, loadDocs]);

  const breadcrumb = useMemo(() => {
    const parts = [selection.ambito === "empresa" ? "Empresa" : selection.licitacionCodigo ?? "Licitaciones"];
    if (selection.subcarpeta) parts.push(subcarpetaLabel(config, selection.subcarpeta));
    return parts.join(" / ");
  }, [selection, config]);

  return (
    <AppShell
      title="Gestión Documental"
      subtitle={`${tree.totalDocumentos} documentos · ${formatBytes(tree.totalBytes)} · almacenamiento: ${config.provider}`}
      actions={
        <UploadDocumentDialog
          config={config}
          ambito={selection.ambito}
          licitacionId={selection.licitacionId}
          defaultSubcarpeta={selection.subcarpeta ?? undefined}
          onUploaded={refresh}
        />
      }
    >
      {!config.storageEnabled ? (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          <HardDrive className="mr-1.5 inline size-3.5" />
          Bucket no configurado. Define <code>GCS_BUCKET</code> en el backend para habilitar la carga de archivos.
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <aside className="rounded-xl border border-border bg-card p-3 lg:sticky lg:top-4 lg:h-fit">
          <div className="mb-1 px-2 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Carpetas
          </div>

          {loadingTree ? (
            <div className="space-y-2 p-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : (
            <nav className="space-y-0.5">
              <TreeItem
                icon={Building2}
                label="Empresa"
                count={tree.empresa.total}
                active={selection.ambito === "empresa" && !selection.subcarpeta}
                onClick={() =>
                  setSelection({ ambito: "empresa", licitacionId: null, licitacionCodigo: null, subcarpeta: null })
                }
              />
              {tree.empresa.subcarpetas.map((sub) => (
                <TreeItem
                  key={sub.subcarpeta}
                  label={sub.label}
                  count={sub.total}
                  nested
                  active={selection.ambito === "empresa" && selection.subcarpeta === sub.subcarpeta}
                  onClick={() =>
                    setSelection({
                      ambito: "empresa",
                      licitacionId: null,
                      licitacionCodigo: null,
                      subcarpeta: sub.subcarpeta,
                    })
                  }
                />
              ))}

              <div className="mt-3 px-2 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Licitaciones
              </div>
              {tree.licitaciones.length === 0 ? (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">
                  Aún no hay licitaciones con documentos.
                </p>
              ) : null}
              {tree.licitaciones.map((lic) => (
                <div key={lic.id}>
                  <TreeItem
                    icon={Folder}
                    label={lic.codigo}
                    hint={lic.nombre}
                    count={lic.total}
                    active={selection.licitacionId === lic.id && !selection.subcarpeta}
                    onClick={() =>
                      setSelection({
                        ambito: "licitacion",
                        licitacionId: lic.id,
                        licitacionCodigo: lic.codigo,
                        subcarpeta: null,
                      })
                    }
                  />
                  {selection.licitacionId === lic.id
                    ? lic.subcarpetas.map((sub) => (
                        <TreeItem
                          key={sub.subcarpeta}
                          label={sub.label}
                          count={sub.total}
                          nested
                          active={selection.subcarpeta === sub.subcarpeta}
                          onClick={() =>
                            setSelection({
                              ambito: "licitacion",
                              licitacionId: lic.id,
                              licitacionCodigo: lic.codigo,
                              subcarpeta: sub.subcarpeta,
                            })
                          }
                        />
                      ))
                    : null}
                </div>
              ))}
            </nav>
          )}
        </aside>

        <section className="space-y-3 lg:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium">{breadcrumb}</p>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label="Buscar documentos"
                placeholder="Buscar por nombre…"
                className="pl-8"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          </div>

          <DocumentoList
            documentos={documentos}
            loading={loadingDocs}
            onChanged={refresh}
            showLicitacion={selection.ambito === "licitacion"}
          />
        </section>
      </div>
    </AppShell>
  );
}

function TreeItem({
  icon: Icon,
  label,
  hint,
  count,
  active,
  nested,
  onClick,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  hint?: string;
  count: number;
  active: boolean;
  nested?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={hint}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition",
        nested && "pl-7 text-xs",
        active
          ? "bg-muted font-medium text-foreground"
          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
      )}
    >
      {Icon ? <Icon className="size-3.5 shrink-0" /> : null}
      <span className="truncate">{label}</span>
      <span className="ml-auto text-[10px] text-muted-foreground">{count}</span>
    </button>
  );
}
